/**
 * PDRS Peak Reduction Certificate scheme registry.
 * Keep in sync with django/oslib/incentive_helpers/prc_calculator.py (PRCSchemes).
 */

export type PRCActivityDefinition = 'bess1' | 'bess2'

export const PRC_SCHEME_COEFFICIENT_FIELDS = [
  'demand_coefficient',
  'modelled_system_lifetime',
  'firmness_factor',
  'peak_reduction_hours',
  'kw_offset_per_certificate',
] as const

export type PRCSchemeCoefficientField = (typeof PRC_SCHEME_COEFFICIENT_FIELDS)[number]

export type PRCSchemeCoefficients = Record<PRCSchemeCoefficientField, number>

export type PRCSchemeConfig = PRCSchemeCoefficients & {
  min_usable_battery_kwh: number
  max_eligible_battery_kwh: number
  credited_battery_cap_kwh: number
}

export const DEFAULT_ACTIVITY_DEFINITION: PRCActivityDefinition = 'bess1'

export const PRC_ACTIVITY_DEFINITION_CHOICES = [
  {
    id: 'bess1' as PRCActivityDefinition,
    name: 'BESS1 - New behind-the-meter battery',
  },
  {
    id: 'bess2' as PRCActivityDefinition,
    name: 'BESS2 - Household demand response (VPP)',
  },
]

export const PRC_SCHEMES: Record<PRCActivityDefinition, PRCSchemeConfig> = {
  bess1: {
    demand_coefficient: 0.0853,
    modelled_system_lifetime: 15,
    firmness_factor: 1,
    peak_reduction_hours: 6,
    kw_offset_per_certificate: 0.1,
    min_usable_battery_kwh: 2,
    max_eligible_battery_kwh: 28,
    credited_battery_cap_kwh: 28,
  },
  bess2: {
    demand_coefficient: 0.0734,
    modelled_system_lifetime: 6,
    firmness_factor: 0.8,
    peak_reduction_hours: 6,
    kw_offset_per_certificate: 0.1,
    min_usable_battery_kwh: 2,
    max_eligible_battery_kwh: 50,
    credited_battery_cap_kwh: 28,
  },
}

/** Keep in sync with PRCNetworkData.LOSS_FACTORS in django/oslib/incentive_helpers/prc_calculator.py */
export const PRC_NETWORK_LOSS_FACTORS = [
  { network: 'Ausgrid', lossFactor: 1.04 },
  { network: 'Endeavour', lossFactor: 1.05 },
  { network: 'Essential Energy', lossFactor: 1.05 },
] as const

export type PRCSettings = {
  activity_definition?: string | null
  /** @deprecated Legacy field — use activity_definition. Read only when migrating saved incentives. */
  is_bess2?: boolean
}

const coerceActivity = (activityDefinition?: string | null): PRCActivityDefinition | null => {
  if (activityDefinition == null || activityDefinition.trim() === '') {
    return null
  }
  const normalised = activityDefinition.trim().toLowerCase()
  if (normalised === 'bess1' || normalised === 'bess2') {
    return normalised
  }
  return null
}

/** Resolve PDRS activity from incentive settings (including legacy is_bess2). */
export const resolveFromSettings = (settings: PRCSettings): PRCActivityDefinition => {
  const activity = coerceActivity(settings.activity_definition)
  if (activity !== null) {
    return activity
  }
  return settings.is_bess2 ? 'bess2' : DEFAULT_ACTIVITY_DEFINITION
}

export const getSchemeCoefficients = (activityDefinition: PRCActivityDefinition): PRCSchemeCoefficients => {
  const scheme = PRC_SCHEMES[activityDefinition]
  const {
    demand_coefficient,
    modelled_system_lifetime,
    firmness_factor,
    peak_reduction_hours,
    kw_offset_per_certificate,
  } = scheme

  return {
    demand_coefficient,
    modelled_system_lifetime,
    firmness_factor,
    peak_reduction_hours,
    kw_offset_per_certificate,
  }
}
